import type { PedigreeType } from '@application/types';
import { enumSelectOptions, pedigreeSelectOptions } from '@application/utils';
import { DogMedicalAffectionType, DogPhysicalCondition } from '@gql/graphql';
import type { BuyerAnimalFormValuesDogInfo, SelectOption } from '@presentation/components';
import {
  AnimalSensitivity,
  InputField,
  InputLabel,
  InputSelectField,
  InputWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { BuyerAnimalFormProps, BuyerAnimalFormValues } from '..';

export const FormDog: FC<BuyerAnimalFormProps> = (props) => {
  const { disabledFields, form, mode } = props;
  const { values } = useFormikContext<BuyerAnimalFormValues>();

  const { translate } = useTranslation();

  const isDisabled = useCallback(
    (key: keyof BuyerAnimalFormValuesDogInfo) => {
      if (mode === 'view') return true;
      if (disabledFields?.dogInfo?.[key] === true) return true;
      return false;
    },
    [disabledFields, mode],
  );

  const conditionOptions = useMemo(() => {
    return enumSelectOptions(
      translate,
      'enum.physicalCondition',
      DogPhysicalCondition,
    ) as SelectOption<DogPhysicalCondition>[];
  }, [translate]);

  const medicalAffectionsOptions = useMemo(() => {
    return enumSelectOptions(
      translate,
      'enum.medicalAffectionType',
      DogMedicalAffectionType,
    ) as SelectOption<DogMedicalAffectionType>[];
  }, [translate]);

  const pedigreeOptions = useMemo(() => {
    return pedigreeSelectOptions(translate, {
      showText: true,
      showIcons: false,
      value: values.dogInfo?.pedigree,
    }) as SelectOption<PedigreeType>[];
  }, [translate, values.dogInfo?.pedigree]);

  return (
    <div>
      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.breedCrossbreed.label')}</InputLabel>
        <InputSelectField<number>
          disabled={isDisabled('breedId')}
          name="dogInfo.breedId"
          options={form.options.breeds as SelectOption<number>[]}
        />
      </InputWrapper>

      <>
        <InputWrapper split>
          <AnimalSensitivity {...props} />
        </InputWrapper>

        <InputWrapper split>
          <InputLabel>{translate('common.inputs.condition.label')}</InputLabel>
          <InputSelectField<DogPhysicalCondition>
            name="dogInfo.physicalCondition"
            disabled={isDisabled('physicalCondition')}
            options={conditionOptions}
          />
        </InputWrapper>

        <InputWrapper split>
          <InputLabel>{translate('common.inputs.medicalAffectations.label')}</InputLabel>
          <InputSelectField<DogMedicalAffectionType>
            name="dogInfo.medicalAffections"
            disabled={isDisabled('medicalAffections')}
            multiple
            dismissOnClick={false}
            options={medicalAffectionsOptions}
          />
        </InputWrapper>
      </>

      {/* Pedigree inputs */}
      <>
        <InputWrapper split>
          <InputLabel>{translate('glossary.pedigree')}</InputLabel>
          <InputSelectField<PedigreeType>
            disabled={isDisabled('pedigree')}
            useSearch={false}
            name="dogInfo.pedigree"
            options={pedigreeOptions}
          />
        </InputWrapper>
        {values.dogInfo?.pedigree != null && (
          <>
            {values.dogInfo?.pedigree !== 'FCI' && (
              <InputWrapper split>
                <InputLabel>{translate('glossary.otherPedigree')}</InputLabel>
                <InputField
                  disabled={isDisabled('pedigree')}
                  name="dogInfo.pedigree"
                  placeholder={translate('common.inputs.pedigreeNumber.placeholder')}
                />
              </InputWrapper>
            )}
            <InputWrapper split last>
              <InputLabel>{translate('common.inputs.pedigreeNumber.placeholder')}</InputLabel>
              <InputField disabled={isDisabled('pedigreeNumber')} name="dogInfo.pedigreeNumber" />
            </InputWrapper>
          </>
        )}
      </>
    </div>
  );
};
